/**
 * Create Manual Journal Entry (UI-16 v3 layout, BE-16 data)
 *
 * Compact form with editable lines table, running totals, and balance check. Accounts kept by their own records are
 * closed to manual lines; VAT accounts ask for the VAT base; a bank / cash line moves that bank's balance. One
 * idempotency key per form, so a double click or a retry never posts twice. TC admin only.
 * Route: /accounting/journal-entries/new
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Alert, IconButton,
  MenuItem, ListSubheader, useTheme,
} from '@mui/material';
import { Add, Delete as DeleteIcon } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { toYmd } from '../../utils/accounting/periodUtils';
import { journalEntryService } from '../../services/accounting/journalEntry.service';
import { serverMessage } from '../../utils/apiConvert';

const emptyLine = () => ({ accountId: '', debit: '', credit: '', description: '', vatBase: '' });
const twoDecimals = (v) => v === '' || /^\d+(\.\d{1,2})?$/.test(String(v));
const isBankOrCash = (number) => {
  const n = Number(number);
  return (n >= 1001 && n <= 1099) || (n >= 1101 && n <= 1199);
};
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `je-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const CreateJournalEntry = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [glAccounts, setGlAccounts] = useState([]);
  const [entryDate, setEntryDate] = useState(toYmd(new Date()));
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [idempotencyKey] = useState(newKey);
  const [searchParams] = useSearchParams();
  const obe = Number(searchParams.get('obe')); // from the health check: clear Opening Balance Equity (3009)

  useEffect(() => {
    journalEntryService.getGlAccounts()
      .then((accounts) => {
        setGlAccounts(accounts);
        const opening = accounts.find((a) => a.accountNumber === '3009');
        if (obe && opening) {
          // a credit balance is cleared by a debit; the other line is left for Owner Capital or Retained Earnings
          const amount = Math.abs(obe).toFixed(2);
          setDescription('Move Opening Balance Equity to owner equity');
          setLines([
            { ...emptyLine(), accountId: opening.id, debit: obe > 0 ? amount : '', credit: obe > 0 ? '' : amount,
              description: 'Clear Opening Balance Equity' },
            { ...emptyLine(), debit: obe > 0 ? '' : amount, credit: obe > 0 ? amount : '',
              description: 'Owner Capital (3001) or Retained Earnings (3003)' },
          ]);
        }
      })
      .catch((err) => setError(serverMessage(err, 'Failed to load accounts')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountOf = (id) => glAccounts.find((a) => a.id === id);

  // A line is a debit or a credit, never both: typing in one side clears the other.
  const updateLine = (idx, field, value) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, [field]: value };
      if (field === 'debit' && value !== '') next.credit = '';
      if (field === 'credit' && value !== '') next.debit = '';
      if (field === 'accountId' && !accountOf(value)?.needsVatBase) next.vatBase = '';
      return next;
    }));
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (idx) => {
    if (lines.length <= 2) return;
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalDebits = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredits = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const difference = Math.abs(totalDebits - totalCredits);
  const isBalanced = difference < 0.005 && totalDebits > 0;
  const filled = lines.filter((l) => l.accountId && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0));
  const amountsValid = lines.every((l) => twoDecimals(l.debit) && twoDecimals(l.credit) && twoDecimals(l.vatBase));
  const vatBaseMissing = filled.some((l) => accountOf(l.accountId)?.needsVatBase && !(parseFloat(l.vatBase) > 0));
  const touchesBank = filled.some((l) => isBankOrCash(accountOf(l.accountId)?.accountNumber));
  const canSave = isBalanced && description.trim() && entryDate && filled.length >= 2 && amountsValid && !vatBaseMissing && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const entry = await journalEntryService.createManual({
        entryDate,
        description: description.trim(),
        idempotencyKey,
        lines: filled.map((l) => {
          const line = { accountId: l.accountId };
          if (parseFloat(l.debit) > 0) line.debit = Number(l.debit);
          else line.credit = Number(l.credit);
          if (l.description.trim()) line.description = l.description.trim();
          if (accountOf(l.accountId)?.needsVatBase) line.vatBase = Number(l.vatBase);
          return line;
        }),
      });
      navigate(entry?.id ? `/accounting/journal-entries/${entry.id}` : '/accounting/journal-entries');
    } catch (err) {
      setError(serverMessage(err, 'Failed to save journal entry'));
    } finally {
      setSaving(false);
    }
  };

  const fmtNum = (n) => n.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const accountTypes = [...new Set(glAccounts.map((a) => a.type))];

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ maxWidth: 900 }}>
        <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 1.5 }}>New Manual Journal Entry</Typography>

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Header fields */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Date" type="date" size="small" value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toYmd(new Date()) }}
            sx={{ width: 180 }}
          />
          <TextField
            label="Description" size="small" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Bank fee on the September statement"
            sx={{ flex: 1, minWidth: 220 }}
          />
        </Box>

        {/* Lines table */}
        <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
          <Paper sx={{ minWidth: 640, border: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, width: '35%', color: 'text.secondary' }}>Account</Typography>
              <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, width: '18%', textAlign: 'right', color: 'text.secondary' }}>Debit</Typography>
              <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, width: '18%', textAlign: 'right', color: 'text.secondary' }}>Credit</Typography>
              <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, width: '25%', color: 'text.secondary' }}>Description</Typography>
              <Box sx={{ width: 36 }} />
            </Box>

            {/* Lines */}
            {lines.map((line, idx) => {
              const acct = accountOf(line.accountId);
              return (
                <Box key={idx} sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      select size="small" value={line.accountId}
                      onChange={(e) => updateLine(idx, 'accountId', e.target.value)}
                      sx={{ width: '35%', '& .MuiInputBase-input': { fontSize: fs.xs } }}
                      SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: fs.xs }}>Select account</MenuItem>
                      {accountTypes.flatMap((type) => [
                        <ListSubheader key={`h-${type}`} sx={{ fontSize: fs.xs, lineHeight: '26px' }}>{type}</ListSubheader>,
                        ...glAccounts.filter((a) => a.type === type).map((a) => (
                          <MenuItem key={a.id} value={a.id} disabled={!a.manualAllowed} sx={{ fontSize: fs.xs }}>
                            {a.accountNumber} - {a.name}{!a.manualAllowed ? ' (kept by its own records)' : ''}
                          </MenuItem>
                        )),
                      ])}
                    </TextField>
                    <TextField
                      size="small" type="number" value={line.debit}
                      onChange={(e) => updateLine(idx, 'debit', e.target.value)}
                      error={!twoDecimals(line.debit)}
                      sx={{ width: '18%', '& .MuiInputBase-input': { fontSize: fs.xs, textAlign: 'right' } }}
                      placeholder="0.00"
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                    <TextField
                      size="small" type="number" value={line.credit}
                      onChange={(e) => updateLine(idx, 'credit', e.target.value)}
                      error={!twoDecimals(line.credit)}
                      sx={{ width: '18%', '& .MuiInputBase-input': { fontSize: fs.xs, textAlign: 'right' } }}
                      placeholder="0.00"
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                    <TextField
                      size="small" value={line.description}
                      onChange={(e) => updateLine(idx, 'description', e.target.value)}
                      sx={{ width: '25%', '& .MuiInputBase-input': { fontSize: fs.xs } }}
                      placeholder="Line note"
                    />
                    <IconButton size="small" onClick={() => removeLine(idx)} disabled={lines.length <= 2}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  {acct?.needsVatBase && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                      <TextField
                        size="small" type="number" label="VAT base" value={line.vatBase}
                        onChange={(e) => updateLine(idx, 'vatBase', e.target.value)}
                        error={!twoDecimals(line.vatBase) || (line.vatBase !== '' && !(parseFloat(line.vatBase) > 0))}
                        sx={{ width: 160, '& .MuiInputBase-input': { fontSize: fs.xs } }}
                        InputLabelProps={{ shrink: true, sx: { fontSize: fs.xs } }}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                      <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>The amount this VAT was charged on (required)</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Paper>
        </Box>

        <Button size="small" startIcon={<Add />} onClick={addLine} sx={{ mb: 2, textTransform: 'none', fontSize: fs.xs }}>
          Add Line
        </Button>

        {touchesBank && (
          <Alert severity="info" sx={{ mb: 2, fontSize: fs.xs }}>
            A bank or cash line moves that account's balance and adds a row to its history. Opening balances are not
            entered here.
          </Alert>
        )}

        {/* Totals */}
        <Paper sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: isBalanced ? 'rgba(34,197,94,0.4)' : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', bgcolor: isBalanced ? (isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)') : 'transparent' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Total Debits</Typography>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>AED {fmtNum(totalDebits)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Total Credits</Typography>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>AED {fmtNum(totalCredits)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>Difference</Typography>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, color: isBalanced ? '#22c55e' : '#ef4444' }}>
                {isBalanced ? 'Balanced' : `AED ${fmtNum(difference)}`}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => navigate('/accounting/journal-entries')} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" size="small" onClick={handleSave}
            disabled={!canSave}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving...' : 'Post Entry'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateJournalEntry;
